import { getConfig } from '@cian/config/browser';
import { type IAddressSuggest, type IMultiSuggestResult } from '@cian/geosuggest-widget';
import { type IHttpApi } from '@cian/http-api/shared';
import { type ILogger } from '@cian/logger';

import { either, isEmpty, isNil, reject } from 'ramda';

import { loadYMapsApi } from '@/packages/YandexMaps/browser';

import { type ISuggestItemsSchema } from '@/repositories/generated/shared/geo-suggest/entities/entities/SuggestItemsSchema';
import { type ISuggestRequest } from '@/repositories/generated/shared/geo-suggest/v2/suggest';

import { fetchSuggestions } from '@/microfrontends/filters/shared/services/fetchSuggestions';

export interface IAddressSuggestionOptions {
  boundedBy: [YMaps.TCoord, YMaps.TCoord] | undefined;
  results: number;
}

export interface IMultiSuggestParams {
  structured: ISuggestRequest;
  yandex: {
    value: string;
    options?: IAddressSuggestionOptions;
  };
}

export interface IResultSuggestions {
  addresses?: IAddressSuggest[];
  cities?: ISuggestItemsSchema['items'];
  districts?: ISuggestItemsSchema['items'];
  newbuildings?: ISuggestItemsSchema['items'];
  undergrounds?: ISuggestItemsSchema['items'];
  villages?: ISuggestItemsSchema['items'];
  roads?: ISuggestItemsSchema['items'];
  railways?: ISuggestItemsSchema['items'];
  regionalDistricts?: ISuggestItemsSchema['items'];
}

export interface IMultiSuggestFull extends Omit<IMultiSuggestResult, 'suggestions'> {
  suggestions: IResultSuggestions;
}

export function getMultiSuggest(
  httpApi: IHttpApi,
  params: IMultiSuggestParams,
  logger: ILogger,
): Promise<IMultiSuggestFull> {
  const {
    yandex: { value, options },
    structured: structuredParams,
  } = params;

  return Promise.all([
    suggest(value, options).catch((ex: Error) => {
      logger.warning(ex, {
        domain: 'microfrontends.filters.shared.containers.LEGACY.GeoSuggest',
        initiator: 'loadYMapsApi',
      });

      return null;
    }),
    fetchSuggestions({ httpApi, logger }, structuredParams),
  ]).then(([yandex, structured]) => {
    if (!yandex || structured.isErr()) {
      throw new Error('Failed to load suggest data');
    }

    let suggestions: IResultSuggestions = {};
    const sortOrder: string[] = [];

    const { cities, districts, newbuildings, undergrounds, railways, roads, villages, regionalDistricts } =
      structured.Ok.data.suggestions;

    suggestions = {
      cities: cities && cities.items,
      districts: districts && districts.items,
      newbuildings: newbuildings && newbuildings.items,
      undergrounds: undergrounds && undergrounds.items,
      villages: villages && villages.items,
      roads: roads && roads.items,
      railways: railways && railways.items,
      regionalDistricts: regionalDistricts && regionalDistricts.items,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    suggestions = reject<any, IResultSuggestions>(either(isNil, isEmpty), suggestions);

    if (structured.Ok.data && !isEmpty(suggestions)) {
      sortOrder.push(...structured.Ok.data.sortOrder);
    }

    if (yandex && !isEmpty(yandex)) {
      suggestions.addresses = (Object.keys(suggestions).length ? yandex.slice(0, 5) : yandex) as IAddressSuggest[];

      if (!sortOrder.includes('addresses')) {
        sortOrder.push('addresses');
      }
    }

    return {
      suggestions,
      sortOrder,
    };
  });
}

export function geocode(request: string, options?: YMaps.IGeocodeOptions): Promise<YMaps.IGeocodeResult> {
  const config = getConfig();
  const yandexApiKey = config.getStrict<string>('audience.YandexApiKey');
  const suggestApiKey = config.getStrict<string>('audience.YandexSuggestApiKey');

  return loadYMapsApi(yandexApiKey, suggestApiKey, ['geocode'])
    .then(ymaps => ymaps.geocode(request, options))
    .then(items => items || []);
}

export function suggest(request: string, options?: YMaps.ISuggestOptions): Promise<YMaps.ISuggestResult[]> {
  const config = getConfig();
  const yandexApiKey = config.getStrict<string>('audience.YandexApiKey');
  const suggestApiKey = config.getStrict<string>('audience.YandexSuggestApiKey');

  return repeatTask(3, () =>
    loadYMapsApi(yandexApiKey, suggestApiKey, ['suggest'])
      .then(ymaps => ymaps.suggest(request, options))
      .then(items => {
        if (isScriptError(items)) {
          throw new Error(items.message);
        }

        return items || [];
      }),
  );
}

function repeatTask<T>(times: number, task: () => Promise<T>): Promise<T> {
  if (times <= 1) {
    return task();
  }

  return task().catch(() => repeatTask(times - 1, task));
}

function isScriptError(item: {}): item is { message: 'scriptError' } {
  return item && 'message' in item && (item as { message?: string }).message === 'scriptError';
}
